import React, { useState } from "react";
import {
  Box,
  Typography,
  Tab,
  Tabs,
  Paper,
  IconButton,
  Snackbar,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  useTheme,
} from "@mui/material";
import {
  ContentCopy as ContentCopyIcon,
  Check as CheckIcon,
  PlayArrow as PlayArrowIcon,
  Code as CodeIcon,
} from "@mui/icons-material";
import Header from "./Header";
import FooterComponent from "./Footer";
import { Light as SyntaxHighlighter } from "react-syntax-highlighter";
import js from "react-syntax-highlighter/dist/esm/languages/hljs/javascript";
import html from "react-syntax-highlighter/dist/esm/languages/hljs/xml";
import typescript from "react-syntax-highlighter/dist/esm/languages/hljs/typescript";
import vs2015 from "react-syntax-highlighter/dist/esm/styles/hljs/vs2015";

SyntaxHighlighter.registerLanguage("javascript", js);
SyntaxHighlighter.registerLanguage("html", html);
SyntaxHighlighter.registerLanguage("typescript", typescript);

const codeSnippets = {
  HTML: {
    language: "html",
    label: "HTML",
    description: "Plain HTML implementation",
    icon: (
      <img
        src="https://img.icons8.com/color/48/000000/html-5--v1.png"
        alt="HTML Icon"
        width={24}
        height={24}
        style={{ verticalAlign: "middle" }}
      />
    ),
    code: `<div id="techrelevance-carousel"></div>
<script src="https://www.career.trueselfy.com/techrelevance-widget.min.js"></script>
<style>
  .swiper-slide {
    background-color: white;
    padding: 10px;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  }
</style>`,
  },
  JavaScript: {
    language: "javascript",
    label: "JavaScript",
    description: "Plain JavaScript DOM-based integration",
    icon: (
      <img
        src="https://img.icons8.com/color/48/000000/javascript--v1.png"
        alt="JavaScript Icon"
        width={24}
        height={24}
        style={{ verticalAlign: "middle" }}
      />
    ),
    code: `<div id="techrelevance-carousel"></div>
<script>
  (function () {
    if (!document.getElementById("techrelevance-widget-script")) {
      const script = document.createElement("script");
      script.src = "https://www.career.trueselfy.com/techrelevance-widget.min.js";
      script.id = "techrelevance-widget-script";
      script.async = true;
      document.body.appendChild(script);
    }
  })();
</script>`,
  },
  React: {
    language: "javascript",
    label: "React",
    description: "React component with hooks",
    icon: (
      <img
        src="https://img.icons8.com/officel/40/react.png"
        alt="React Icon"
        width={24}
        height={24}
        style={{ verticalAlign: "middle" }}
      />
    ),
    code: `import { useEffect } from "react";

const TechRelevanceWidget = () => {
  useEffect(() => {
    if (!document.getElementById("techrelevance-widget-script")) {
      const script = document.createElement("script");
      script.src = "https://www.career.trueselfy.com/techrelevance-widget.min.js";
      script.id = "techrelevance-widget-script";
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);

  return <div id="techrelevance-carousel" />;
};

export default TechRelevanceWidget;`,
  },
  Vue: {
    language: "html",
    label: "Vue.js",
    description: "Vue.js component implementation",
    icon: (
      <img
        src="https://img.icons8.com/color/48/000000/vue-js.png"
        alt="Vue.js Icon"
        width={24}
        height={24}
        style={{ verticalAlign: "middle" }}
      />
    ),
    code: `<template>
  <div id="techrelevance-carousel"></div>
</template>

<script>
export default {
  name: "TechRelevanceWidget",
  mounted() {
    if (!document.getElementById("techrelevance-widget-script")) {
      const script = document.createElement("script");
      script.src = "https://www.career.trueselfy.com/techrelevance-widget.min.js";
      script.id = "techrelevance-widget-script";
      script.async = true;
      document.body.appendChild(script);
    }
  }
};
</script>`,
  },
  Angular: {
    language: "typescript",
    label: "Angular",
    description: "Angular component with TypeScript",
    icon: (
      <img
        src="https://img.icons8.com/color/48/000000/angularjs.png"
        alt="Angular Icon"
        width={24}
        height={24}
        style={{ verticalAlign: "middle" }}
      />
    ),
    code: `import { Component, AfterViewInit } from '@angular/core';

@Component({
  selector: 'app-tech-widget',
  template: '<div id="techrelevance-carousel"></div>',
  styleUrls: ['./tech-widget.component.css']
})
export class TechWidgetComponent implements AfterViewInit {
  ngAfterViewInit(): void {
    if (!document.getElementById("techrelevance-widget-script")) {
      const script = document.createElement("script");
      script.src = "https://www.career.trueselfy.com/techrelevance-widget.min.js";
      script.id = "techrelevance-widget-script";
      script.async = true;
      document.body.appendChild(script);
    }
  }
}`,
  },
  NextJS: {
    language: "javascript",
    label: "Next.js",
    description: "Next.js component with dynamic imports",
    icon: (
      <img
        src="https://img.icons8.com/?size=100&id=MWiBjkuHeMVq&format=png&color=000000"
        alt="Next.js Icon"
        width={24}
        height={24}
        style={{ verticalAlign: "middle", }}
      />
    ),
    code: `import { useEffect } from 'react';
import dynamic from 'next/dynamic';

const TechRelevanceWidget = () => {
  useEffect(() => {
    const loadWidget = async () => {
      if (!document.getElementById("techrelevance-widget-script")) {
        const script = document.createElement("script");
        script.src = "https://www.career.trueselfy.com/techrelevance-widget.min.js";
        script.id = "techrelevance-widget-script";
        script.async = true;
        document.body.appendChild(script);
      }
    };
    
    loadWidget();
  }, []);

  return <div id="techrelevance-carousel" />;
};

export default dynamic(() => Promise.resolve(TechRelevanceWidget), {
  ssr: false
});`,
  },
};


const TechRelevanceEmbedShowcase = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [copySuccess, setCopySuccess] = useState(false);
  const [copied, setCopied] = useState(false);
  const theme = useTheme();

  const frameworks = Object.keys(codeSnippets);
  const currentFramework = frameworks[selectedTab];
  const currentSnippet = codeSnippets[currentFramework];

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(currentSnippet.code);
      setCopySuccess(true);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const handleCloseSnackbar = () => {
    setCopySuccess(false);
  };

  return (
    <React.Fragment>
      <Header />
      <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
        {/* Header Section */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <CodeIcon sx={{ fontSize: 32, color: 'primary.main' }} />
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 700,
                color: 'text.primary',
                background: 'linear-gradient(45deg, #1976d2, #42a5f5)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Embed TechRelevance Carousel
            </Typography>
          </Box>

          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ mb: 3, maxWidth: 600 }}
          >
            Integrate the TechRelevance widget seamlessly into your website.
            Choose your preferred framework and copy the ready-to-use code snippet.
          </Typography>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              label="Easy Integration"
              color="primary"
              variant="filled"
              size="small"
            />
            <Chip
              label="Responsive Design"
              color="success"
              variant="outlined"
              size="small"
            />
            <Chip
              label="Cross-Platform"
              color="info"
              variant="outlined"
              size="small"
            />
          </Box>
        </Box>

        {/* Main Content */}
        <Paper
          elevation={3}
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          {/* Framework Tabs */}
          <Box sx={{ bgcolor: 'grey.50', borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={selectedTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                '& .MuiTab-root': {
                  minHeight: 72,
                  fontWeight: 500,
                  textTransform: 'none',
                  fontSize: '0.95rem',
                  py: 2,
                  minWidth: 120,
                },
                '& .Mui-selected': {
                  bgcolor: 'background.paper',
                  borderRadius: '8px 8px 0 0',
                  margin: '4px 4px 0 4px',
                },
              }}
            >
              {frameworks.map((framework) => (
                <Tab
                  key={framework}
                  label={
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h6" sx={{ mb: 0.5 }}>
                        {codeSnippets[framework].icon}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {codeSnippets[framework].label}
                      </Typography>
                      {/* <Typography variant="caption" color="text.secondary">
                        {codeSnippets[framework].description}
                      </Typography> */}
                    </Box>
                  }
                />
              ))}
            </Tabs>
          </Box>

          {/* Code Display Area */}
          <Box sx={{ position: 'relative', bgcolor: '#1e1e1e' }}>
            {/* Header Bar with Copy Button */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                px: 2,
                py: 1,
                bgcolor: 'rgba(255, 255, 255, 0.05)',
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontFamily: 'monospace',
                  textTransform: 'uppercase',
                  letterSpacing: 1,
                }}
              >
                {currentSnippet.label} Implementation
              </Typography>

              <IconButton
                onClick={handleCopy}
                size="small"
                sx={{
                  color: copied ? 'success.light' : 'rgba(255, 255, 255, 0.7)',
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
              </IconButton>
            </Box>

            {/* Syntax Highlighter */}
            <SyntaxHighlighter
              language={currentSnippet.language}
              style={vs2015}
              customStyle={{
                margin: 0,
                padding: '24px',
                fontSize: '14px',
                lineHeight: '1.6',
                borderRadius: 0,
                minHeight: '300px',
                background: 'transparent',
              }}
              showLineNumbers={true}
              lineNumberStyle={{
                color: 'rgba(255, 255, 255, 0.3)',
                fontSize: '12px',
                marginRight: '24px',
                userSelect: 'none',
              }}
              wrapLines={true}
            >
              {currentSnippet.code}
            </SyntaxHighlighter>
          </Box>
        </Paper>

        {/* Integration Steps */}
        {/* <Box sx={{ mt: 4 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          🚀 Quick Integration Guide
        </Typography>
        
        <Paper elevation={1} sx={{ p: 3, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
          <List dense>
            <ListItem>
              <ListItemIcon>
                <PlayArrowIcon sx={{ color: 'primary.contrastText' }} />
              </ListItemIcon>
              <ListItemText 
                primary="Copy the code snippet for your preferred framework"
                secondary="Select the tab above and click the copy button"
                secondaryTypographyProps={{ sx: { color: 'rgba(255, 255, 255, 0.7)' } }}
              />
            </ListItem>
            <Divider sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', my: 1 }} />
            <ListItem>
              <ListItemIcon>
                <PlayArrowIcon sx={{ color: 'primary.contrastText' }} />
              </ListItemIcon>
              <ListItemText 
                primary="Paste it into your project"
                secondary="Add the code where you want the carousel to appear"
                secondaryTypographyProps={{ sx: { color: 'rgba(255, 255, 255, 0.7)' } }}
              />
            </ListItem>
            <Divider sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', my: 1 }} />
            <ListItem>
              <ListItemIcon>
                <PlayArrowIcon sx={{ color: 'primary.contrastText' }} />
              </ListItemIcon>
              <ListItemText 
                primary="Ensure external scripts are allowed"
                secondary="Make sure your CSP allows loading from techrelevance.trueselfy.com"
                secondaryTypographyProps={{ sx: { color: 'rgba(255, 255, 255, 0.7)' } }}
              />
            </ListItem>
            <Divider sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', my: 1 }} />
            <ListItem>
              <ListItemIcon>
                <PlayArrowIcon sx={{ color: 'primary.contrastText' }} />
              </ListItemIcon>
              <ListItemText 
                primary="Widget auto-renders on page load"
                secondary="The carousel will automatically initialize when ready"
                secondaryTypographyProps={{ sx: { color: 'rgba(255, 255, 255, 0.7)' } }}
              />
            </ListItem>
          </List>
        </Paper>
      </Box> */}

        {/* Important Notes */}
        <Box sx={{ mt: 3 }}>
          <Alert
            severity="info"
            sx={{
              borderRadius: 2,
              '& .MuiAlert-message': {
                width: '100%'
              }
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              📝 Important Notes:
            </Typography>
            <Typography variant="body2" component="div">
              • Include the widget script only <strong>once per page</strong> to avoid conflicts
              <br />
              • The carousel automatically handles responsive design
              <br />
              • Customize styling using CSS to match your brand
              <br />
              • For React/Next.js: Component handles script loading automatically
            </Typography>
          </Alert>
        </Box>

        {/* Success Snackbar */}
        <Snackbar
          open={copySuccess}
          autoHideDuration={3000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseSnackbar} severity="success" variant="filled" >
            Code copied to clipboard! 🎉
          </Alert>
        </Snackbar>
      </Box>
      <FooterComponent />
    </React.Fragment>
  );
};

export default TechRelevanceEmbedShowcase;